import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Wrench } from 'lucide-react';
import { fmtDate, formatINR } from '@/lib/format';
import { toast } from 'sonner';

const COLUMNS = ['submitted', 'assigned', 'in_progress', 'completed', 'closed'];
const LABELS = { submitted: 'Submitted', assigned: 'Assigned', in_progress: 'In progress', completed: 'Completed', closed: 'Closed' };
const PRIORITY_CLS = {
  p1: 'bg-[hsl(0_84%_56%/0.10)] text-[hsl(0_75%_48%)] border-[hsl(0_84%_56%/0.25)]',
  p2: 'bg-[hsl(34_92%_45%/0.12)] text-[hsl(34_85%_36%)] border-[hsl(34_92%_45%/0.25)]',
  p3: 'bg-muted text-muted-foreground border',
};

export default function Maintenance() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ status: '', priority: '', assigned_vendor: '', estimated_cost: 0, actual_cost: 0, closure_note: '' });

  const load = () => api.get('/maintenance').then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const open = (t) => { setEdit(t); setForm({ status: t.status, priority: t.priority, assigned_vendor: t.assigned_vendor || '', estimated_cost: t.estimated_cost, actual_cost: t.actual_cost, closure_note: t.closure_note || '' }); };
  const submit = async () => {
    try {
      await api.put(`/maintenance/${edit.id}`, { ...form, estimated_cost: Number(form.estimated_cost), actual_cost: Number(form.actual_cost) });
      toast.success('Ticket updated'); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const grouped = COLUMNS.reduce((acc, c) => ({ ...acc, [c]: items.filter(i => i.status === c) }), {});

  return (
    <div>
      <PageHeader title="Maintenance" subtitle="Drag along the workflow — update status as work progresses" />
      <div className="flex gap-3 overflow-x-auto pb-3" data-testid="maintenance-kanban-board">
        {COLUMNS.map((c) => (
          <div key={c} className="min-w-[280px] md:flex-1 rounded-xl border bg-muted/30 p-2" data-testid={`maintenance-kanban-column-${c}`}>
            <div className="flex items-center justify-between px-2 py-1.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{LABELS[c]}</div>
              <span className="text-xs text-muted-foreground">{grouped[c].length}</span>
            </div>
            <div className="space-y-2">
              {grouped[c].map((t) => (
                <Card key={t.id} className="p-3 card-hover cursor-pointer" onClick={() => open(t)} data-testid={`maintenance-card-${t.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-sm">{t.title}</div>
                    <span className={`text-[10px] uppercase rounded-full border px-1.5 py-0.5 ${PRIORITY_CLS[t.priority] || ''}`}>{t.priority}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{t.ticket_number} · {t.unit_number}</div>
                  <div className="text-xs mt-1.5">{t.tenant_name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{fmtDate(t.created_at)} · {formatINR(t.estimated_cost || t.actual_cost)}</div>
                </Card>
              ))}
              {!grouped[c].length && <div className="text-xs text-muted-foreground py-6 text-center">—</div>}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Wrench className="h-4 w-4" /> Update ticket</DialogTitle></DialogHeader>
          {edit && (
            <div className="text-sm space-y-3">
              <div className="text-muted-foreground">{edit.title} · {edit.unit_number}</div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Status</Label><Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid="maintenance-status-select"><SelectValue /></SelectTrigger><SelectContent>{COLUMNS.map(c => <SelectItem key={c} value={c}>{LABELS[c]}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Priority</Label><Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="p1">P1 - Critical</SelectItem><SelectItem value="p2">P2 - High</SelectItem><SelectItem value="p3">P3 - Normal</SelectItem></SelectContent></Select></div>
                <div className="col-span-2"><Label>Vendor</Label><Input value={form.assigned_vendor} onChange={(e) => setForm({ ...form, assigned_vendor: e.target.value })} /></div>
                <div><Label>Estimated cost</Label><Input type="number" value={form.estimated_cost} onChange={(e) => setForm({ ...form, estimated_cost: e.target.value })} /></div>
                <div><Label>Actual cost</Label><Input type="number" value={form.actual_cost} onChange={(e) => setForm({ ...form, actual_cost: e.target.value })} /></div>
                <div className="col-span-2"><Label>Closure note</Label><Input value={form.closure_note} onChange={(e) => setForm({ ...form, closure_note: e.target.value })} /></div>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={submit} data-testid="maintenance-update-submit">Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
